import { useI18n } from '@ecom/i18n';
import { LanguageSwitcher } from '@ecom/ui-kit';
import { motion } from 'framer-motion';
import { type ReactElement, type ReactNode } from 'react';

import { useLocale } from '@/lib/locale';

/**
 * The frame of every signed-out MERCHANT screen (Login, password change, reset,
 * forgot). It carries the language switcher: a person who has just arrived from
 * an email (password reset) is signed out and has no other way to choose Arabic,
 * French or English.
 *
 * Visual identity: ABOUEB (أبواب — E-commerce Solutions) — blue + navy. The
 * brand blue is scoped to `.brand-auth` (styles/globals.css) so it recolours
 * these pre-auth screens only, never the Souk-Ember tokens the authenticated app
 * is built on. The merchant side wears the brighter, "welcome" blue that tells
 * it apart from the admin console while keeping one identity.
 */

const LOGO_SRC = `${import.meta.env.BASE_URL}aboueb-logo.jpg`;

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  icon,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
  /** An optional status illustration shown above the title. */
  icon?: ReactNode;
}): ReactElement {
  const { locale, setLocale } = useLocale();
  const { t } = useI18n();

  return (
    <div className="brand-auth grid min-h-dvh bg-ground lg:grid-cols-[1fr_1.05fr]">
      {/* ---------------------------------------------------------- brand panel */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#163a86] via-[#2470be] to-[#46b3e6] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-12">
        <div className="pointer-events-none absolute -right-20 top-10 h-80 w-80 rounded-full bg-white/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-4 h-72 w-72 rounded-full bg-[#9bd9f0]/30 blur-3xl" />
        <div className="pointer-events-none absolute -left-28 top-1/3 h-96 w-96 rounded-full border-[14px] border-white/10" />

        <span className="relative z-10 flex h-[4.5rem] w-fit items-center rounded-2xl bg-white px-4 shadow-lg">
          <img src={LOGO_SRC} alt="ABOUEB" className="h-14 w-auto object-contain" />
        </span>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative z-10 max-w-md"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            {t('merchant.authBrand.space')}
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight xl:text-4xl">
            {t('merchant.authBrand.headline')}
            <span className="block text-[#eaf6fd]">{t('merchant.authBrand.headlineAccent')}</span>
          </h2>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/85">
            {t('merchant.authBrand.lead')}
          </p>
        </motion.div>

        <p className="relative z-10 text-xs text-white/70">
          © {new Date().getFullYear()} ABOUEB — E-commerce Solutions
        </p>
      </aside>

      {/* ------------------------------------------------------------- form side */}
      <main className="flex min-h-dvh flex-col px-5 py-6 sm:px-10">
        <div className="flex justify-end">
          <LanguageSwitcher locale={locale} onChange={setLocale} />
        </div>

        <div className="flex flex-1 items-center justify-center py-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="w-full max-w-md"
          >
            {/* brand header — shown when the side panel is hidden (tablet/mobile) */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <span className="flex h-14 items-center rounded-xl border border-line bg-surface px-3 shadow-card">
                <img src={LOGO_SRC} alt="ABOUEB" className="h-10 w-auto object-contain" />
              </span>
              <span className="text-sm font-semibold text-inkSoft">
                {t('merchant.authBrand.space')}
              </span>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-7 shadow-card sm:p-8">
              {icon !== undefined ? <div className="mb-4 flex justify-center">{icon}</div> : null}
              <div
                className={
                  icon !== undefined
                    ? 'mx-auto mb-6 h-1 w-10 rounded-full bg-accent'
                    : 'mb-6 h-1 w-10 rounded-full bg-accent'
                }
                aria-hidden="true"
              />
              <h1
                className={
                  icon !== undefined
                    ? 'text-center font-display text-2xl font-bold text-ink'
                    : 'font-display text-2xl font-bold text-ink'
                }
              >
                {title}
              </h1>
              <p
                className={
                  icon !== undefined
                    ? 'mt-1.5 text-center text-sm text-inkFaint'
                    : 'mt-1.5 text-sm text-inkFaint'
                }
              >
                {subtitle}
              </p>
              <div className="mt-6">{children}</div>
            </div>

            <div className="mt-5 text-center text-sm text-inkFaint">{footer}</div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
